import React from 'react';
import { useNavigate } from 'react-router-dom';

const FEATURES = [
    { icon: '🧠', title: 'Algorithm Intelligence', desc: 'Our experts study Instagram\'s algorithm daily. We know what signals to trigger, when to post, and how to maximise organic reach — no guesswork.', addon: false },
    { icon: '🤖', title: 'AI-Guided Strategy', desc: 'We use AI to analyse your profile, niche, and competitors. Every action is data-driven and calibrated to push your content to the right audience.', addon: false },
    { icon: '💬', title: 'Genuine Engagement', desc: 'Our team interacts with real profiles in your niche. Comments, follows, DMs — all 100% human, zero bots, zero automation tools.', addon: false },
    { icon: '📈', title: 'Growth Reporting', desc: 'Weekly reports with follower growth, reach, engagement rate, and next-week strategy — powered by real data and AI insights.', addon: false },
    { icon: '🎯', title: 'Niche Targeting', desc: 'We research your audience and engage only with profiles that match your ideal customer profile. Precision over volume.', addon: false },
    { icon: '📅', title: 'Content Creation', desc: 'Need posts, reels, and stories? We can evaluate a full content calendar tailored to your brand. Ask us — we may be able to help.', addon: true },
];

const PLANS = [
    {
        name: 'Starter',
        price: '€299',
        period: '/mo',
        features: ['500 new followers/month', 'Daily likes on 5–10 posts', '1 story posted/week', 'Bi-weekly report'],
        cta: 'Apply for Starter',
    },
    {
        name: 'Growth',
        price: '€599',
        period: '/mo',
        hot: true,
        features: ['1,500 followers/month', 'Daily likes + AI comments', '3 stories/week', 'Weekly report', '30-min weekly call'],
        cta: 'Apply for Growth',
    },
    {
        name: 'VIP',
        price: '€999',
        period: '/mo',
        features: ['3,000+ followers/month', 'AI-optimised daily engagement', 'Full niche targeting + competitor analysis', 'Weekly strategy calls', 'Priority support + dedicated manager'],
        cta: 'Apply for VIP',
    },
];

const s = {
    root:    { minHeight: '100vh', background: 'linear-gradient(135deg, #0a0d14 0%, #0f1320 50%, #0a0d14 100%)', color: '#f1f5f9', fontFamily: '"Segoe UI", system-ui, sans-serif', position: 'relative', overflow: 'hidden', margin: -24 },
    glow:    { position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse, rgba(109,40,217,0.18) 0%, transparent 70%)', pointerEvents: 'none' },
    inner:   { maxWidth: 1100, margin: '0 auto', padding: '80px 48px' },
    badge:   { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', background: 'rgba(109,40,217,0.25)', border: '1px solid rgba(139,92,246,0.4)', borderRadius: 100, fontSize: 11, fontWeight: 800, color: '#c4b5fd', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 20 },
    logoRow: { display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24, flexWrap: 'wrap' },
    h1:      { fontSize: 48, fontWeight: 900, lineHeight: 1.1, marginBottom: 16, letterSpacing: '-1.5px', background: 'linear-gradient(135deg, #e9d5ff, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    sub:     { fontSize: 17, color: '#94a3b8', lineHeight: 1.7, maxWidth: 540, marginBottom: 40 },
    featGrid:{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 64, marginBottom: 72 },
    featCard:{ background: 'rgba(109,40,217,0.06)', border: '1px solid rgba(109,40,217,0.2)', borderRadius: 16, padding: '24px 20px' },
    planGrid:{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 48, paddingTop: 16 },
    planCard:{ background: 'rgba(109,40,217,0.06)', border: '1px solid rgba(109,40,217,0.2)', borderRadius: 20, padding: '32px 24px', position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'visible' },
    planHot: { border: '1px solid rgba(139,92,246,0.55)', background: 'rgba(109,40,217,0.14)' },
    hotBdg:  { position: 'absolute', top: 12, right: 16, background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', fontSize: 10, fontWeight: 800, padding: '4px 12px', borderRadius: 100, whiteSpace: 'nowrap', letterSpacing: 0.5 },
    planName:{ fontSize: 20, fontWeight: 800, color: '#e9d5ff', marginBottom: 4 },
    planAmt: { fontSize: 44, fontWeight: 900, color: '#c4b5fd', letterSpacing: '-2px', lineHeight: 1 },
    planPer: { fontSize: 13, color: '#64748b', marginLeft: 2 },
    planList:{ listStyle: 'none', padding: 0, margin: '16px 0 24px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
    planLi:  { fontSize: 13, color: '#94a3b8', display: 'flex', alignItems: 'flex-start', gap: 8 },
    planChk: { color: '#a78bfa', fontWeight: 700, minWidth: 14, marginTop: 1 },
    cta:     { width: '100%', padding: '12px 0', border: '1px solid rgba(139,92,246,0.5)', background: 'transparent', color: '#c4b5fd', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .2s', textAlign: 'center' },
    ctaHot:  { background: 'linear-gradient(135deg, #6d28d9, #7c3aed)', border: 'none', color: '#fff' },
    divider: { borderTop: '1px solid rgba(109,40,217,0.2)', paddingTop: 40, textAlign: 'center' },
    note:    { fontSize: 13, color: '#475569', marginTop: 16 },
};

export default function Zenith() {
    const navigate = useNavigate();

    return (
        <div style={s.root}>
            <div style={s.glow} />
            <div className="og-zenith-inner" style={s.inner}>

                {/* Header */}
                <div style={s.logoRow}>
                    <img src="/zenith-logo.svg" alt="Zenith" style={{ height: 120, width: 'auto', filter: 'brightness(0) invert(1)' }} />
                    <span style={s.badge}>Coming Soon</span>
                </div>

                <h1 className="og-zenith-h1" style={s.h1}>Real Experts &amp; AI.<br />Zero Bots. Real Growth.</h1>
                <p style={s.sub}>
                    Zenith is <strong style={{ color: '#c4b5fd' }}>not a bot, not an automation tool</strong> — it's a team of social media experts guided by AI, studying the Instagram algorithm every day to push your account forward. You keep full control of your account; we handle strategy and execution.
                </p>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <a href="https://t.me/orbitgrowth" target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', background: 'linear-gradient(135deg, #6d28d9, #7c3aed)', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 20px rgba(109,40,217,0.4)' }}>
                        📩 Express Interest on Telegram
                    </a>
                    <button onClick={() => navigate('/dashboard')}
                        style={{ padding: '12px 24px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 10, fontSize: 14, cursor: 'pointer' }}>
                        ← Back to Dashboard
                    </button>
                </div>

                {/* Features grid */}
                <div style={s.featGrid} className="og-zenith-feat-grid">
                    {FEATURES.map(f => (
                        <div key={f.title} style={{ ...s.featCard, ...(f.addon ? { opacity: 0.75, border: '1px solid rgba(109,40,217,0.15)' } : {}) }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ fontSize: 28 }}>{f.icon}</div>
                                {f.addon && <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: 'rgba(109,40,217,0.15)', border: '1px solid rgba(109,40,217,0.3)', borderRadius: 20, padding: '2px 10px', letterSpacing: 1, textTransform: 'uppercase' }}>Add-on</span>}
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#e9d5ff', marginBottom: 8 }}>{f.title}</div>
                            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65 }}>{f.desc}</div>
                        </div>
                    ))}
                </div>

                {/* Plans */}
                <h2 style={{ fontSize: 32, fontWeight: 800, color: '#e9d5ff', marginBottom: 32, letterSpacing: '-0.5px' }}>Plans & Pricing</h2>
                <div style={s.planGrid} className="og-zenith-plans">
                    {PLANS.map(p => (
                        <div key={p.name} style={{ ...s.planCard, ...(p.hot ? s.planHot : {}) }}>
                            {p.hot && <div style={s.hotBdg}>Most Popular</div>}
                            <div style={s.planName}>{p.name}</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 4 }}>
                                <span style={s.planAmt}>{p.price}</span>
                                <span style={s.planPer}>{p.period}</span>
                            </div>
                            <ul style={s.planList}>
                                {p.features.map(f => (
                                    <li key={f} style={s.planLi}>
                                        <span style={s.planChk}>✓</span><span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <a href="https://t.me/orbitgrowth" target="_blank" rel="noopener noreferrer"
                                style={{ ...s.cta, ...(p.hot ? s.ctaHot : {}), textDecoration: 'none', display: 'block' }}>
                                {p.cta}
                            </a>
                        </div>
                    ))}
                </div>

                {/* Bottom note */}
                <div style={s.divider}>
                    <p style={{ fontSize: 15, color: '#94a3b8' }}>
                        Service is in <strong style={{ color: '#c4b5fd' }}>early access</strong> — limited spots available.
                    </p>
                    <p style={s.note}>
                        Contact us on Telegram <a href="https://t.me/orbitgrowth" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>@orbitgrowth</a> to get on the waitlist.
                        Full onboarding within 48h · Monthly rolling contract · Cancel anytime.
                    </p>
                    <p style={{ ...s.note, marginTop: 24, color: '#1e293b' }}>
                        This is a <strong style={{ color: '#334155' }}>separate service</strong> from OrbitGrowth automation — Zenith uses no bots or automation tools.
                    </p>
                </div>
            </div>
        </div>
    );
}
